import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { customSafariService } from '../services/customSafariService';
import {
    MapIcon,
    CalendarIcon,
    TrashIcon,
    PencilIcon,
    ArrowLeftIcon,
    PlusCircleIcon
} from '@heroicons/react/24/outline';

const SavedSafaris: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const { actualTheme } = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [savedSafaris, setSavedSafaris] = useState<any[]>([]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        const fetchSafaris = async () => {
            try {
                if (user) {
                    const cartItems = await customSafariService.getCart(user.id);
                    setSavedSafaris(cartItems || []);
                }
            } catch (error) {
                console.error('Error loading safaris:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSafaris();
    }, [isAuthenticated, navigate, user]);

    const handleDelete = async (cartItemId: string) => {
        if (window.confirm('Are you sure you want to delete this safari?')) {
            try {
                if (user) {
                    await customSafariService.removeFromCart(cartItemId, user.id);
                    // Refresh the list
                    const cartItems = await customSafariService.getCart(user.id);
                    setSavedSafaris(cartItems || []);
                }
            } catch (error) {
                console.error('Error deleting safari:', error);
            }
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
            }`}>
            {/* Header */}
            <div className={`backdrop-blur-md shadow-modern sticky top-0 z-50 transition-all duration-300 ${actualTheme === 'dark'
                ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-b border-slate-600/30'
                : 'bg-white/95 border-b border-neutral-200/50'
                }`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <button
                            onClick={() => navigate('/')}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-modern text-sm font-medium border transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                                ? 'text-slate-300 border-slate-600/30 hover:text-slate-200 hover:border-slate-500/50'
                                : 'text-primary-600 border-primary-200 hover:text-primary-700 hover:border-primary-300'
                                }`}
                        >
                            <ArrowLeftIcon className="w-4 h-4" />
                            <span>Back to Home</span>
                        </button>
                        <h1 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>My Saved Safaris</h1>
                        <button
                            onClick={() => navigate('/tours')}
                            className="px-4 py-2 rounded-modern text-sm font-medium transition-all duration-300 hover-magnify-sm modern-focus bg-primary-600 hover:bg-primary-700 text-white"
                        >
                            <PlusCircleIcon className="w-4 h-4 inline mr-2" />
                            Create New
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : savedSafaris.length === 0 ? (
                    <div className={`text-center py-12 rounded-2xl shadow-modern ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
                        }`}>
                        <MapIcon className={`w-16 h-16 mx-auto mb-4 ${actualTheme === 'dark' ? 'text-slate-600' : 'text-gray-400'
                            }`} />
                        <h3 className={`text-xl font-semibold mb-2 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>No Saved Safaris Yet</h3>
                        <p className={`mb-6 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                            }`}>Start building your dream safari adventure!</p>
                        <button
                            onClick={() => navigate('/tours')}
                            className="px-6 py-3 rounded-modern font-medium transition-all duration-300 hover-magnify-sm bg-primary-600 hover:bg-primary-700 text-white"
                        >
                            Browse Tours
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {savedSafaris.map((item: any) => (
                            <div
                                key={item.id}
                                className={`rounded-2xl shadow-modern backdrop-blur-sm border transition-all duration-300 hover:shadow-xl ${actualTheme === 'dark'
                                    ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border-slate-600/30'
                                    : 'bg-white/90 border-neutral-200/50'
                                    }`}
                            >
                                <div className="p-6">
                                    <h3 className={`text-lg font-bold mb-2 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                                        }`}>{item.itinerary?.name || 'Unnamed Safari'}</h3>

                                    <div className="space-y-2 mb-4">
                                        <div className="flex items-center text-sm">
                                            <CalendarIcon className={`w-4 h-4 mr-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                                                }`} />
                                            <span className={actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>
                                                {item.itinerary?.tours?.length || 0} tours
                                            </span>
                                        </div>
                                        <div className="flex items-center text-sm">
                                            <MapIcon className={`w-4 h-4 mr-2 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                                                }`} />
                                            <span className={actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'}>
                                                {item.itinerary?.totalDuration || 0} days
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => navigate(`/?safari=${item.itinerary?.id}`)}
                                            className="flex-1 px-4 py-2 rounded-modern text-sm font-medium transition-all duration-300 hover-magnify-sm bg-primary-600 hover:bg-primary-700 text-white"
                                        >
                                            <PencilIcon className="w-4 h-4 inline mr-1" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(item.id)}
                                            className={`px-4 py-2 rounded-modern text-sm font-medium transition-all duration-300 hover-magnify-sm ${actualTheme === 'dark'
                                                ? 'bg-red-900/20 hover:bg-red-900/40 text-red-400'
                                                : 'bg-red-50 hover:bg-red-100 text-red-600'
                                                }`}
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SavedSafaris;
